import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { IApiError } from '~/types/api'

import { MODAL_ID } from '..'

import Button from '~/components/Buttons/Button'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import { FORM_ERRORS } from '~/const'
import { keys as catalogKeys, useAddPostToCategoryMutation, useCatalogCategoriesListQuery } from '~/queries/catalog'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { displayToast } from '~/utils/toast'
import { YupOption } from '~/utils/yup'

interface Values {
  category: INullableOption
}

const validationSchema = Yup.object().shape({
  category: YupOption.required(FORM_ERRORS.REQUIRED),
})

interface Props {
  onClose: VoidFunction
  selectedTemplate: string
}

const CategoryForm = ({ onClose, selectedTemplate }: Props): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    resetField,
  } = useForm<Values>({
    resolver: yupResolver(validationSchema),
  })

  /** Queries & Mutations */

  const { data: categories } = useCatalogCategoriesListQuery()

  const categoriesOption = categories
    ?.map(({ category_name }) => category_name)
    .sort((a, b) => (a > b ? 1 : -1))
    .map((el) => ({
      label: el,
      value: el,
    }))

  const { mutateAsync: addPostToCategoryMutation } = useAddPostToCategoryMutation()

  /** Handlers */

  const handleFormSubmit = async (values: Values) => {
    try {
      await addPostToCategoryMutation({
        category_name: values.category.value,
        enabled: true,
        post_id: selectedTemplate,
      })
      await queryClient.invalidateQueries({ queryKey: catalogKeys.searchTemplates('') })
      await queryClient.invalidateQueries({ queryKey: catalogKeys.categoryPosts(values.category.value) })
      onClose()
      displayToast(`You successfully added the template to the category ${values.category?.value}`, 'success')
      resetField('category')
    } catch (e) {
      captureException(e)

      if (isAxiosError(e)) {
        const errorData = e.response?.data as IApiError

        switch (errorData.kind) {
          case 'already_exists':
            displayToast(`Template already exists in the category "${values.category.value}"`, 'error')
            break
          case 'missing_param':
            displayToast(`Please select a category"`, 'error')
            break
          default:
            displayToast(`Template could not be added to the category "${values.category.value}"`, 'error')
        }
      } else {
        displayToast('Something went wrong', 'error')
      }
    }
  }

  /** Render */

  return (
    <>
      <ModalHeader>Add to Category</ModalHeader>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <ModalBody>
          <Controller
            control={control}
            name="category"
            render={({ field }) => (
              <SelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                error={errors?.category?.message}
                label="Category"
                options={categoriesOption}
                required
              />
            )}
          />
        </ModalBody>
        <ModalFooter>
          <Button ghost onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={isSubmitting} type="submit">
            Add
          </Button>
        </ModalFooter>
      </form>
      <DevTool control={control} />
    </>
  )
}

export default CategoryForm
